import { useState } from 'react';
import { Grid, Card, CardContent, Typography, Box, Chip, Alert, IconButton } from '@mui/material';
import { keyframes } from '@mui/system';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MetricExplanationModal from './MetricExplanationModal';

// Icon mapping for equipment types
const equipmentIcons = {
  Chiller: '❄️',
  RTU: '🏢',
  CRAC: '🖥️',
  Split: '💨',
  Unknown: '❓',
};

// Staggered card reveal
const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * EquipmentTypeBreakdown - By-Type ROI Metrics
 *
 * Displays ROI metrics grouped by equipment type (Chiller, RTU, CRAC, Split).
 * Shows count, tonnage, cost, savings, payback, and ROI for each type.
 */
export default function EquipmentTypeBreakdown({ data }) {
  const [openMetric, setOpenMetric] = useState(null);

  if (!data) {
    console.error('EquipmentTypeBreakdown: Missing required data prop');
    return null;
  }

  if (Object.keys(data).length === 0) {
    console.error('EquipmentTypeBreakdown: Empty data object');
    return (
      <Alert severity="info" sx={{ mt: 3 }}>
        No equipment type breakdown data available
      </Alert>
    );
  }

  // Format helpers
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value, decimals = 0) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  // Filter out types with no equipment
  const activeTypes = Object.entries(data).filter(([_, metrics]) => metrics.count > 0);

  return (
    <Box sx={{ my: 4 }}>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.01em',
            mb: 0.5,
          }}
        >
          Equipment Type Breakdown
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ROI metrics by equipment category
        </Typography>
      </Box>

      {/* Equipment Type Cards */}
      <Grid container spacing={3}>
        {activeTypes.map(([type, metrics], idx) => (
          <Grid
            item
            xs={12}
            sm={6}
            lg={3}
            key={type}
            sx={{
              animation: `${fadeSlideUp} 0.5s ease-out`,
              animationDelay: `${0.1 * (idx + 1)}s`,
              animationFillMode: 'backwards',
            }}
          >
            <Card
              sx={{
                height: '100%',
                bgcolor: 'background.paper',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  borderColor: 'rgba(160, 206, 78, 0.4)',
                  boxShadow: '0 12px 32px rgba(160, 206, 78, 0.15), 0 0 0 1px rgba(160, 206, 78, 0.1)',
                  transform: 'translateY(-2px)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #A0CE4E, #4ECDC4)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Header: Icon + Type */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Typography variant="h4" sx={{ fontSize: '2rem', lineHeight: 1 }}>
                    {equipmentIcons[type] || equipmentIcons.Unknown}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        letterSpacing: '-0.01em',
                        color: 'text.primary',
                      }}
                    >
                      {type}{metrics.count > 1 ? 's' : ''}
                    </Typography>
                    <Chip
                      label={`${metrics.count} unit${metrics.count !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        mt: 0.5,
                        backgroundColor: 'rgba(160, 206, 78, 0.15)',
                        color: (theme) => theme.palette.mode === 'dark' ? '#C5E87D' : 'text.primary',
                        border: '1px solid rgba(160, 206, 78, 0.3)',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>

                {/* Metrics */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {/* Tonnage */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                      Total Tonnage
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary', fontFamily: '"JetBrains Mono", monospace' }}>
                      {formatNumber(metrics.total_tonnage, 1)} tons
                    </Typography>
                  </Box>

                  {/* Treatment Cost */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                      Treatment Cost
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: (theme) => theme.palette.mode === 'dark' ? '#FF6B35' : 'text.primary', fontFamily: '"JetBrains Mono", monospace' }}>
                      {formatCurrency(metrics.total_treatment_cost)}
                    </Typography>
                  </Box>

                  {/* Annual Savings */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                        Annual Savings
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setOpenMetric('per_type_savings')}
                        sx={{ p: 0.25, color: 'rgba(140, 155, 165, 0.6)', '&:hover': { color: (theme) => theme.palette.mode === 'dark' ? '#4ECDC4' : 'text.primary' } }}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: (theme) => theme.palette.mode === 'dark' ? '#4ECDC4' : 'text.primary', fontFamily: '"JetBrains Mono", monospace' }}>
                      {formatCurrency(metrics.total_annual_savings)}
                    </Typography>
                  </Box>

                  {/* Payback */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      pt: 1.5,
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          Payback
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setOpenMetric('per_type_payback')}
                          sx={{ p: 0.25, color: 'rgba(140, 155, 165, 0.6)', '&:hover': { color: (theme) => theme.palette.mode === 'dark' ? '#4ECDC4' : 'text.primary' } }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontFamily: '"JetBrains Mono", monospace' }}>
                        {formatNumber(metrics.average_payback_months, 1)} mo
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          5-Yr ROI
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setOpenMetric('per_type_roi')}
                          sx={{ p: 0.25, color: 'rgba(140, 155, 165, 0.6)', '&:hover': { color: (theme) => theme.palette.mode === 'dark' ? '#A0CE4E' : 'text.primary' } }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: (theme) => theme.palette.mode === 'dark' ? '#A0CE4E' : 'text.primary', fontFamily: '"JetBrains Mono", monospace' }}>
                        {formatNumber(metrics.five_year_roi, 0)}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* kWh Saved */}
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      borderRadius: 1,
                      background: 'rgba(78, 205, 196, 0.08)',
                      border: '1px solid rgba(78, 205, 196, 0.2)',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                      Annual kWh Saved
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: (theme) => theme.palette.mode === 'dark' ? '#4ECDC4' : 'text.primary', fontFamily: '"JetBrains Mono", monospace' }}>
                      {formatNumber(metrics.total_kwh_saved)} kWh
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      {formatNumber(metrics.co2_avoided_tonnes, 1)} tonnes CO₂
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Metric Explanation Modal */}
      <MetricExplanationModal
        metric={openMetric}
        open={Boolean(openMetric)}
        onClose={() => setOpenMetric(null)}
      />
    </Box>
  );
}
